import {
    serializable,
    DynamicFormControlLayout,
    DynamicInputModel,
    DynamicInputModelConfig,
	MATCH_VISIBLE,
} from "@ng-dynamic-forms/core";
import { Model_WorkflowComponent, CODE_WORKFLOW_COMPONENT, TYPE_WORKFLOW_COMPONENT_OBSERVER, TYPE_DEFAULT_VALUE, TYPE_DATASOURCE } from 'src/app/app.models';
import { COMPONENT_MODE } from '../workflow-components.models';
import { WorkflowComponentBuilderOptions } from '../workflow-components.service';

export class Model_WorkflowInputGridComponent extends Model_WorkflowComponent {
	GRID_COLUMNS?: Model_WorkflowComponent[];
	IS_ROWS_EDITABLE?: boolean;
	IS_ROWS_DELETABLE?: boolean;
	BTN_ADD_ROWS?: boolean;
}
  
export interface InputGridModelConfig extends DynamicInputModelConfig {
	componentMode: COMPONENT_MODE;
	gridColumns: Model_WorkflowComponent[];
	workflowComponentBuilderOptions: WorkflowComponentBuilderOptions;
	isRowsEditable?: boolean;
	isRowsDeletable?: boolean;
	btnAddRows?: boolean;
}
  
export class InputGridModel extends DynamicInputModel {
	@serializable() readonly type: string = 'input_grid';
	componentMode: COMPONENT_MODE;
	gridColumns: Model_WorkflowComponent[];
	workflowComponentBuilderOptions: WorkflowComponentBuilderOptions;
	isRowsEditable = false;
	isRowsDeletable = false;
	btnAddRows = true;

	constructor(config: InputGridModelConfig, layout?: DynamicFormControlLayout) {
		super(config, layout);
		this.componentMode = config.componentMode;
		this.gridColumns = config.gridColumns;
		this.workflowComponentBuilderOptions = config.workflowComponentBuilderOptions;
		this.isRowsEditable = config.isRowsEditable == true;
		this.isRowsDeletable = config.isRowsDeletable == true;
		this.btnAddRows = config.btnAddRows == true;
	}

	static initModel(_component: Model_WorkflowInputGridComponent, _options: WorkflowComponentBuilderOptions): InputGridModel {
		const formModel = new InputGridModel({
			id: _component.ID,
			name: _component.ID,
			label: _component.LABEL,
			value: _component.DEFAULT_VALUE,
			required: _component.IS_REQUIRED,
			readOnly: _component.IS_READONLY,
			hidden: _component.IS_VISIBLE !== undefined ? _component.IS_VISIBLE === false : false,
			validators: _component.CUSTOM_VALIDATORS,
			componentMode: _component.COMPONENT_MODE,
			gridColumns: _component.GRID_COLUMNS,
            additional: _component,
			relations: _component.RELATIONS,
			workflowComponentBuilderOptions: _options,
			isRowsEditable: _component.IS_ROWS_EDITABLE,
			isRowsDeletable: _component.IS_ROWS_DELETABLE,
			btnAddRows: _component.BTN_ADD_ROWS === undefined || _component.BTN_ADD_ROWS === true
		});
		return formModel;
	}

	static getProperties(_options?: WorkflowComponentBuilderOptions): Model_WorkflowComponent {
		return {
			ID: 'input-grid',
			CODE: CODE_WORKFLOW_COMPONENT.INPUT_GRID,
			ICON: 'grid-outline',
			LABEL: 'Grille de saisie',
			HAS_DATASOURCE: true,
			PROPERTIES: [
				{ ID: 'GUID', CODE: CODE_WORKFLOW_COMPONENT.TEXTBOX, LABEL: 'GUID', IS_VISIBLE: false },
				{ ID: 'INDEX', CODE: CODE_WORKFLOW_COMPONENT.NUMERIC_TEXTBOX, LABEL: 'Index', DEFAULT_VALUE: 1 },
				{ ID: 'ID', CODE: CODE_WORKFLOW_COMPONENT.TEXTBOX, LABEL: 'Identifiant', IS_REQUIRED: true, CUSTOM_VALIDATORS: [{ VALIDATOR_NAME: 'no_whitespace' }] },
				{ ID: 'LABEL', CODE: CODE_WORKFLOW_COMPONENT.TEXTBOX, LABEL: 'Libellé', IS_REQUIRED: true },
				{ ID: 'IS_VISIBLE', CODE: CODE_WORKFLOW_COMPONENT.TOGGLE, LABEL: 'Est Visible ?', DEFAULT_VALUE: true },
				{ ID: 'IS_REQUIRED', CODE: CODE_WORKFLOW_COMPONENT.TOGGLE, LABEL: 'Est Requis ?' },
				{ ID: 'IS_READONLY', CODE: CODE_WORKFLOW_COMPONENT.TOGGLE, LABEL: 'Lecture Seule ?' },
				{ ID: 'IS_ROWS_EDITABLE', CODE: CODE_WORKFLOW_COMPONENT.TOGGLE, LABEL: 'Lignes Modifiables ?', DEFAULT_VALUE: true, RELATIONS: [{ match: MATCH_VISIBLE, when: [{ id: 'IS_READONLY', value: false }] }]   },
				{ ID: 'IS_ROWS_DELETABLE', CODE: CODE_WORKFLOW_COMPONENT.TOGGLE, LABEL: 'Lignes Supprimables ?', DEFAULT_VALUE: true, RELATIONS: [{ match: MATCH_VISIBLE, when: [{ id: 'IS_READONLY', value: false }] }]   },
				{ ID: 'BTN_ADD_ROWS', CODE: CODE_WORKFLOW_COMPONENT.TOGGLE, LABEL: 'Bouton \'Ajouter une ligne\' ?', DEFAULT_VALUE: true },
				{ ID: 'GRID_COLUMNS', CODE: CODE_WORKFLOW_COMPONENT.INPUT_GRID_COLUMNS_BUILDER, LABEL: 'Colonnes', IS_ROWS_EDITABLE: true, IS_ROWS_DELETABLE: true },
				{ ID: 'HAS_DATASOURCE', CODE: CODE_WORKFLOW_COMPONENT.TOGGLE, LABEL: 'Mapper une source de données ?' },
				{ 
					ID: 'NUM_DATASOURCE', 
					CODE: CODE_WORKFLOW_COMPONENT.DATASOURCE_PICKER, 
					LABEL: 'Source de données', 
					RELATIONS: [{ match: MATCH_VISIBLE, when: [{ id: 'HAS_DATASOURCE', value: true }] }]  
				},
				{ 
					ID: 'GRID_COLUMNS_DATASOURCE_MAPPING', 
					CODE: CODE_WORKFLOW_COMPONENT.INPUT_GRID_COLUMNS_DATASOURCE_MAPPING, 
					LABEL: 'Correspondance des colonnes', 
					RELATIONS: [{ match: MATCH_VISIBLE, when: [{ id: 'HAS_DATASOURCE', value: true }] }],
					UIC_PARAMS: {
						OBSERVERS: [
							{ field: 'GRID_COLUMNS', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.ASSIGN_GRID_COLUMNS }
						]
					} 
				},
				{ ID: 'GRID_COLUMNS_DATASOURCE_MAPPING_MODE', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Mode de remplissage', DEFAULT_VALUE: 1, IS_REQUIRED: true, 
					UIC_PARAMS: {
						DATASOURCE: {
							DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
							DATASOURCE_DATAS: [
								{ ID: 1, TEXT: 'Automatique' }, 
								{ ID: 2, TEXT: 'Sélection manuelle' }, 
							],
							DATASOURCE_VALUE_FIELD: 'ID',
							DATASOURCE_DISPLAY_FIELD: 'TEXT'
						}
					},
					RELATIONS: [{ match: MATCH_VISIBLE, when: [{ id: 'HAS_DATASOURCE', value: true }] }],
				},
			]
		};
	}
}
  